/**
 * 같은 브라우저의 다른 탭에 "저장했음" 신호를 보낸다(BroadcastChannel, 서버 요청·폴링 없음).
 * 받은 탭은 같은 이름의 화면 이벤트를 다시 쏴서, 그 탭의 화면이 캐시/서버에서 다시 읽게 한다.
 * BroadcastChannel 이 없는 환경(구형 브라우저·테스트)에서는 아무것도 하지 않는다.
 */

const CHANNEL_NAME = 'cp-sync';

export type CrossTabSaveSignalKind = 'program-progress-saved' | 'annual-timetable-saved';

type CrossTabMessage = {
  kind: CrossTabSaveSignalKind;
  detail: Record<string, unknown>;
  senderId: string;
};

type Handler = (detail: Record<string, unknown>) => void;

const senderId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const handlers = new Map<CrossTabSaveSignalKind, Set<Handler>>();
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    channel = null;
    return channel;
  }
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent) => {
      const msg = ev.data as CrossTabMessage | null;
      if (!msg || typeof msg !== 'object' || msg.senderId === senderId) return;
      const set = handlers.get(msg.kind);
      if (!set || !msg.detail || typeof msg.detail !== 'object') return;
      set.forEach((h) => {
        try {
          h(msg.detail);
        } catch {
          /* 한 화면 오류가 다른 화면 갱신을 막지 않게 */
        }
      });
    };
  } catch {
    channel = null;
  }
  return channel;
}

/** 다른 탭에 저장 신호 보내기 (이 탭에는 전달되지 않음) */
export function postCrossTabSaveSignal(kind: CrossTabSaveSignalKind, detail: object): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    const msg: CrossTabMessage = { kind, detail: { ...(detail as Record<string, unknown>) }, senderId };
    ch.postMessage(msg);
  } catch {
    /* 직렬화 불가 등은 무시 */
  }
}

/** 다른 탭에서 온 저장 신호 받기. 해제 함수 반환 */
export function subscribeCrossTabSaveSignal(kind: CrossTabSaveSignalKind, handler: Handler): () => void {
  getChannel();
  let set = handlers.get(kind);
  if (!set) {
    set = new Set();
    handlers.set(kind, set);
  }
  set.add(handler);
  return () => {
    handlers.get(kind)?.delete(handler);
  };
}
